import React, { useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReportContext } from '../context/ReportProvider';
import CustomReport from '../CustomReport/CustomReport';
import ExportDropdown from '../ExportDropdown/ExportDropdown';
import './ReportFilterDisplayStyle.css';
import { IMemorizedReportResponse } from '../Layouts/StandardReportLayout/IMemorizedReportResponse';
import { removeMemIdParam } from '../../../utils/removeMemIdParams';
import { FilterOperator, operatorSymbols } from './FilterOperation';
import { RedisCacheObject } from '../../../provider/RedisProvider';
import { store } from '../../../stores/store';
import { toJS } from 'mobx';
import { IDepartment, IHospital } from './IFilterHospitalDept';

interface FiltersDisplayProps {
    reportId?: number;
    holderId?: number;
    userId?: number;
    memId: string | false;
    memReportResponse: IMemorizedReportResponse | undefined
}

// Type for structured filter
interface ActiveFilter {
    field: string;
    value: any;
    operator: FilterOperator;
    symbol: string;
}

const consignedOwnedValueMap: Record<string, string> = {
    '0': 'Owned',
    '1': 'Consigned',
    '2': 'Both'
};

const MAX_INLINE_VALUES = 2;

// Function to get display value for a filter
// This function formats the value for display, handling arrays and strings
function getDisplayValue(value: string | number | (string | number)[]): { text: string; overflow: number } {
    let items: (string | number)[] = [];

    if (Array.isArray(value)) {
        items = value;
    } else if (typeof value === 'string') {
        // Split string by commas and trim
        items = value.split(',').map(item => item.trim()).filter(item => item !== '');
    } else {
        // Single number or string
        items = [value];
    }

    const maxVisible = MAX_INLINE_VALUES;
    const visibleItems = items.slice(0, maxVisible);
    const overflowCount = items.length - maxVisible;

    return {
        text: visibleItems.join(', '),
        overflow: overflowCount > 0 ? overflowCount : 0
    };
}

// Function to format filter label
// This function formats the filter key to a more readable label
function formatFilterLabel(key: string): JSX.Element {
    const cleanedKey = key.replace(/id/gi, '').replace(/_/g, '').replace(/([a-z])([A-Z])/g, '$1 $2');
    const formattedLabel = cleanedKey.charAt(0).toUpperCase() + cleanedKey.slice(1);
    return <strong>{formattedLabel}</strong>;
}

const ReportFiltersDisplay: React.FC<FiltersDisplayProps> = ({
    reportId,
    holderId,
    userId,
    memId,
    memReportResponse
}) => {
    const reportContext = useContext(ReportContext);
    const [queryParams, setQueryParams] = useSearchParams();
    const [guardMessage, setGuardMessage] = useState<string | null>(null);
    const triggerGuardToast = (msg = 'At least one must remain selected') => {
        setGuardMessage(msg);
        window.clearTimeout((triggerGuardToast as any)._t);
        (triggerGuardToast as any)._t = window.setTimeout(() => setGuardMessage(null), 2000);
    };

    const { commonStore } = store;
    const deployEnv = (((window as any)?.RUNTIME_CONFIG?.DEPLOY_ENV) || process.env.REACT_APP_DEPLOY_ENV || '').toString().toLowerCase();
    const isProd = deployEnv === 'prod' || deployEnv === 'production';
    const exportObjectType = isProd ? 'QsightPrdlegUsageTransfer' : 'UsageTransfer';

    const initialParams = {
        departmentId: [commonStore.departmentId].filter((id): id is number => id !== undefined),
        hospitalId: [commonStore.hospitalId].filter((id): id is number => id !== undefined),
    };

    const defaultFilters: Record<string, any> = {
        hospitalId: initialParams.hospitalId[0],
        departmentId: initialParams.departmentId[0],
        orderBy: '',
    };

    const redisCache = toJS(commonStore.redisCache) as RedisCacheObject;

    const [hospitals] = React.useState<IHospital[]>(redisCache?.Value?.Authorized?.Hospitals || []);
    const [departments] = React.useState<IDepartment[]>(redisCache?.Value?.Authorized?.HospitalDepartments || []);

    // Create maps for HospitalId and DepartmentId to their names
    // This allows us to quickly look up names based on IDs in the filters
    const hospitalMap = React.useMemo(() => {
        const map: Record<string, string> = {};
        hospitals.forEach(h => {
            map[h.HospitalId] = h.HospitalName;
        });
        return map;
    }, [hospitals]);

    const departmentMap = React.useMemo(() => {
        const map: Record<string, string> = {};
        departments.forEach(d => {
            map[d.DepartmentId] = d.DepartmentName;
        });
        return map;
    }, [departments]);

    const reportFilter = Object.keys(reportContext?.reportFilter ?? {}).length > 0 ? reportContext?.reportFilter : {};

    // Function to handle removing a filter
    // This function updates the report filter and query parameters when a filter is removed
    const handleRemoveFilter = (key: string) => {

        const updatedFilter: Record<string, any> = { ...reportFilter };

        const lowerKey = key.toLowerCase();
        if (lowerKey === 'hospitalid' || lowerKey === 'departmentid') {
            // Block removal to enforce golden rule; show toast and exit
            triggerGuardToast(`At least one ${lowerKey === 'hospitalid' ? 'hospital' : 'department'} must remain selected`);
            return;
        }

        // Normalize key for ConsignedOrOwned
        const normalizedKey = key === 'ConsignedOrOwned' ? 'consigned' : key;

        if (normalizedKey in updatedFilter) {
            // Direct key in reportFilter
            delete updatedFilter[normalizedKey];
        } else if (Array.isArray(updatedFilter.queryBuilder)) {
            const filteredQueryBuilder = updatedFilter.queryBuilder.filter(
                (query: any) => {
                    const fieldName = query.fieldName === 'consigned' ? 'ConsignedOrOwned' : query.fieldName;
                    return fieldName !== key;
                }
            );

            if (filteredQueryBuilder.length > 0) {
                updatedFilter.queryBuilder = filteredQueryBuilder;
            } else {
                delete updatedFilter.queryBuilder;
            }
        }

        if (
            updatedFilter &&
            typeof updatedFilter === 'object' &&
            Object.keys(updatedFilter).length === 1 &&
            'queryBuilder' in updatedFilter &&
            Array.isArray(updatedFilter.queryBuilder) &&
            updatedFilter.queryBuilder.length === 0
        ) {
            delete updatedFilter.queryBuilder; // after this, updatedFilter becomes an empty object {}
        }

        // Keep hospital/department in URL based on the updated filter or fall back to logged-in defaults
        // Preserve memid if user is editing a memorized report
        const updatedParams = reportContext?.isEditingMemorized ? new URLSearchParams(queryParams) : removeMemIdParam(queryParams);
        const nextHospitalIds = Array.isArray((updatedFilter as any).hospitalId) && (updatedFilter as any).hospitalId.length > 0
            ? (updatedFilter as any).hospitalId.map(String)
            : [initialParams.hospitalId[0]].filter(Boolean).map(String);
        const nextDepartmentIds = Array.isArray((updatedFilter as any).departmentId) && (updatedFilter as any).departmentId.length > 0
            ? (updatedFilter as any).departmentId.map(String)
            : [initialParams.departmentId[0]].filter(Boolean).map(String);

        if (nextHospitalIds.length > 0) {
            updatedParams.set('hospitalId', nextHospitalIds.join(','));
        }
        if (nextDepartmentIds.length > 0) {
            updatedParams.set('departmentId', nextDepartmentIds.join(','));
        }
        // Ensure memid stays in place during edit mode
        if (reportContext?.isEditingMemorized && memId && memId !== 'false') {
            updatedParams.set('memid', String(memId));
        }
        setQueryParams(updatedParams);
        reportContext?.setReportFilter(updatedFilter);
        if (reportContext?.setMemorizedData) {
            reportContext.setMemorizedData({});
        }
    };

    // Function to handle clearing all filters
    // This function resets the report filter and query parameters to their initial state
    const handleClearAllFilters = () => {
        // Preserve memid if user is editing a memorized report
        const updatedParams = reportContext?.isEditingMemorized ? new URLSearchParams(queryParams) : removeMemIdParam(queryParams);
        setQueryParams(updatedParams);

        // Always default hospital/department back to logged-in values
        const keepHospitalIds = [initialParams.hospitalId[0]].filter(Boolean).map(String);
        const keepDepartmentIds = [initialParams.departmentId[0]].filter(Boolean).map(String);

        reportContext?.setReportFilter({
            hospitalId: keepHospitalIds,
            departmentId: keepDepartmentIds,
            orderBy: '',
            queryBuilder: [],
        } as any);
        // Ensure memid stays in place during edit mode
        if (reportContext?.isEditingMemorized && memId && memId !== 'false') {
            updatedParams.set('memid', String(memId));
            setQueryParams(updatedParams);
        }
        if (reportContext?.setMemorizedData) {
            reportContext.setMemorizedData({});
        }
    };

    const simplifyDisplayValue = (key: string, value: any): string => {
        if (key.toLowerCase() === 'hospitalid') {
            return Array.isArray(value)
                ? value.map((id: string) => hospitalMap[id] ?? id).join(', ')
                : hospitalMap[value] ?? value;
        }

        if (key.toLowerCase() === 'departmentid') {
            return Array.isArray(value)
                ? value.map((id: string) => departmentMap[id] ?? id).join(', ')
                : departmentMap[value] ?? value;
        }

        if (key.toLowerCase() === 'consigned') {
            return Array.isArray(value)
                ? value.map((v: number | string) => consignedOwnedValueMap[String(v)] ?? v).join(', ')
                : consignedOwnedValueMap[String(value)] ?? value;
        }

        return Array.isArray(value) ? value.join(', ') : value;
    };

    // Create a list of active filters based on the reportFilter
    // This list is used to display the currently applied filters in the UI
    const toActiveFilters = (filterObj: Record<string, any>): ActiveFilter[] =>
        Object.entries(filterObj ?? {}).flatMap(([key, value]) => {
            if (key === 'queryBuilder' && Array.isArray(value)) {
                return value.map((query) => {
                    const operator = query.operator as FilterOperator;
                    const fieldKey = query.fieldName;
                    const displayValue = simplifyDisplayValue(query.fieldName, query.value);

                    return {
                        field: fieldKey,
                        value: displayValue,
                        operator,
                        symbol: operatorSymbols[operator] ?? ':',
                    };
                });
            }

            // Validate non-queryBuilder key/value
            const isValid = Array.isArray(value) ? value.length > 0 : value !== undefined && value !== '';
            if (!isValid) return [];

            // Map 'consigned' to UI field 'ConsignedOrOwned'
            const fieldKey = key === 'consigned' ? 'ConsignedOrOwned' : key;
            const displayValue = simplifyDisplayValue(key, value);

            return [
                {
                    field: fieldKey,
                    value: displayValue,
                    operator: FilterOperator.EQUALS,
                    symbol: operatorSymbols[FilterOperator.EQUALS],
                },
            ];
        });

    // Build filters from current reportFilter
    const activeFilters: ActiveFilter[] = toActiveFilters(reportFilter ?? {});

    // Build filters from default values (used when activeFilters is empty)
    const defaultActiveFilters: ActiveFilter[] = toActiveFilters(defaultFilters);

    // If there are no active filters, use defaults for display.
    const displayFilters: ActiveFilter[] =
        activeFilters.length > 0 ? activeFilters : defaultActiveFilters;


    return (
        <div className="filterDisplayContainer">
            {guardMessage ? (
                <div className="alert alert-warning py-2 px-3 mb-2" role="alert">
                    {guardMessage}
                </div>
            ) : null}
            {/* Toolbar (label + clear on left, actions on right) */}
            <div className="filterToolbar" role="region" aria-label="Filter toolbar">
                <div className="toolbarLeft">
                    <span className="appliedFiltersLabel">Applied Filters</span>
                    <button
                        type="button"
                        className="linkButton clearAll"
                        onClick={handleClearAllFilters}
                        aria-label="Clear all applied filters"
                    >
                        <i className="bi bi-trash3 ml-1 mr-1" />
                        Clear All
                    </button>
                </div>

                <div className="toolbarRight">
                    <CustomReport
                        qSightReportId={reportId}
                        qSightHolderId={holderId}
                        qSightUserId={userId}
                        currReportMemId={memId}
                        memReportResponse={memReportResponse}
                    />
                    {reportContext?.isEditingMemorized && memId && memId !== 'false' ? (
                        <button
                            type="button"
                            className="btn btn-link ml-2"
                            onClick={() => {
                                const updated = removeMemIdParam(queryParams);
                                reportContext?.setIsEditingMemorized?.(false);
                                setQueryParams(updated);
                            }}
                        >
                            Exit edit mode
                        </button>
                    ) : null}
                    <ExportDropdown objectType={exportObjectType} />
                </div>
            </div>

            <div className="filterChipsContainer" role="list" aria-label="Applied filter chips">
                {/* When no active filters exist, display default filters instead of "No filters applied." */}
                {displayFilters.length === 0 ? (
                    <div className="noFilters text-secondary">No filters applied.</div>
                ) : (
                    displayFilters.map((filter, index) => {
                        const { field, value, symbol } = filter;
                        const { text, overflow } = getDisplayValue(value);

                        return (
                            <div key={`${field}-${index}`} className="filterBox" role="listitem">
                                <span className="filterLabel">
                                    <strong>{formatFilterLabel(field)}</strong> {symbol} {text}{' '}
                                    {overflow > 0 && <span className="overflowBadge">+{overflow}</span>}
                                </span>

                                <button
                                    type="button"
                                    className="removeButton"
                                    onClick={() => handleRemoveFilter(field)}
                                    aria-label={`Remove filter ${formatFilterLabel(field)}`}
                                    title={`Remove ${formatFilterLabel(field)}`}
                                >
                                    x
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default ReportFiltersDisplay;